/* ----------------------------------------------------------------------------
switch between light and dark mode: wraps a <button>, and each click flips the
mode the page is showing, whether that came from the system or a saved choice.
Saves it as `mode`, like theme-picker.js, so theme.js applies it before first
paint on the next page. Leaves any color theme alone.
---------------------------------------------------------------------------- */

const root = document.documentElement;
const systemDark = window.matchMedia('(prefers-color-scheme: dark)');

// the mode on screen: a saved .light or .dark class, otherwise the system's
const currentMode = () => {
	if (root.classList.contains('dark')) return 'dark';
	if (root.classList.contains('light')) return 'light';
	return systemDark.matches ? 'dark' : 'light';
};

export default class ThemeToggle extends HTMLElement {
	constructor() {
		super();
		this.handleClick = this.handleClick.bind(this);
		this.render = this.render.bind(this);
	}

	connectedCallback() {
		this.button = this.querySelector('button');
		this.state = this.querySelector('[data-state]');
		if (!this.button) return;
		this.button.addEventListener('click', this.handleClick);
		systemDark.addEventListener('change', this.render);
		this.render();
	}

	disconnectedCallback() {
		this.button?.removeEventListener('click', this.handleClick);
		systemDark.removeEventListener('change', this.render);
	}

	handleClick() {
		const mode = currentMode() === 'dark' ? 'light' : 'dark';
		root.classList.remove('light', 'dark');
		root.classList.add(mode);
		try {
			window.localStorage.setItem('mode', mode);
		} catch {
			// storage can be blocked: the mode still applies to this page
		}
		this.render();
	}

	// tell screen readers which mode is on; the circle shows it through CSS
	render() {
		if (this.state) this.state.textContent = `, ${currentMode()}`;
	}
}

window.customElements.define('theme-toggle', ThemeToggle);
